import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ExpandMore, Description, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { apiService } from '@services/api';
import logger from '@utils/logger';

export interface TermDocument {
  _id: string;
  type: 'terms_of_use' | 'lgpd' | 'resale_prohibition' | 'other';
  title: string;
  content: string;
  version: string;
  displayOrder: number;
}

interface TermsAcceptanceProps {
  onAcceptanceChange: (acceptedTermIds: string[]) => void;
  /** Chamado quando o estado "todos aceitos" muda (para habilitar/desabilitar o botão Criar conta) */
  onAllAccepted?: (allAccepted: boolean) => void;
  required?: boolean;
}

export const TermsAcceptance: React.FC<TermsAcceptanceProps> = ({
  onAcceptanceChange,
  onAllAccepted,
  required = true,
}) => {
  const [terms, setTerms] = useState<TermDocument[]>([]);
  const [acceptedTermIds, setAcceptedTermIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);

  useEffect(() => {
    loadTerms();
  }, []);

  useEffect(() => {
    onAcceptanceChange(Array.from(acceptedTermIds));
  }, [acceptedTermIds, onAcceptanceChange]);

  useEffect(() => {
    if (onAllAccepted) {
      const allAccepted = terms.length > 0 && terms.every((t) => acceptedTermIds.has(t._id));
      onAllAccepted(allAccepted);
    }
  }, [terms, acceptedTermIds, onAllAccepted]);

  const loadTerms = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getRequiredTerms();
      if (response.data.success) {
        setTerms(response.data.data);
      } else {
        setError('Erro ao carregar termos. Tente novamente.');
      }
    } catch (err: unknown) {
      logger.error('Error loading terms', err);
      setError('Erro ao carregar termos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleTermToggle = (termId: string) => {
    setAcceptedTermIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(termId)) {
        newSet.delete(termId);
      } else {
        newSet.add(termId);
      }
      return newSet;
    });
  };

  const handleAcceptAll = () => {
    const allIds = new Set(terms.map((t) => t._id));
    setAcceptedTermIds(allIds);
  };

  const handleOpenDialog = (termId: string) => {
    setOpenDialog(termId);
  };

  const handleCloseDialog = () => {
    setOpenDialog(null);
  };

  const handleAccordionChange = (termId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? termId : false);
  };

  const getTermTypeLabel = (type: string) => {
    switch (type) {
      case 'terms_of_use':
        return 'Termo de Uso';
      case 'lgpd':
        return 'LGPD';
      case 'resale_prohibition':
        return 'Proibição de Revenda';
      default:
        return 'Documento';
    }
  };

  const selectedTerm = terms.find((t) => t._id === openDialog);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (terms.length === 0) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Nenhum termo disponível no momento.
      </Alert>
    );
  }

  const allAccepted = terms.length > 0 && terms.every((t) => acceptedTermIds.has(t._id));

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Termos e Condições
        </Typography>
        {!allAccepted && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleAcceptAll}
            startIcon={<CheckCircle />}
          >
            Aceitar Todos
          </Button>
        )}
      </Box>

      {required && !allAccepted && (
        <Alert severity="warning" icon={<ErrorIcon />} sx={{ mb: 2 }}>
          É obrigatório aceitar todos os termos para criar uma conta.
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        {terms.map((term) => {
          const isAccepted = acceptedTermIds.has(term._id);
          return (
            <Accordion
              key={term._id}
              expanded={expandedAccordion === term._id}
              onChange={handleAccordionChange(term._id)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  backgroundColor: isAccepted ? 'success.light' : 'background.paper',
                  '&:hover': {
                    backgroundColor: isAccepted ? 'success.light' : 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mr: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAccepted}
                        onChange={() => handleTermToggle(term._id)}
                        onClick={(e) => e.stopPropagation()}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {term.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getTermTypeLabel(term.type)} • Versão {term.version}
                        </Typography>
                      </Box>
                    }
                    sx={{ flex: 1, m: 0 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Description />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDialog(term._id);
                    }}
                    sx={{ ml: 2 }}
                  >
                    Ver Completo
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    pr: 2,
                    '& h1, & h2, & h3': {
                      mt: 2,
                      mb: 1,
                      fontWeight: 'bold',
                    },
                    '& p': {
                      mb: 1,
                    },
                    '& ul': {
                      mb: 1,
                      pl: 3,
                    },
                    '& li': {
                      mb: 0.5,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: term.content }}
                />
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Dialog para visualização completa */}
      <Dialog
        open={openDialog !== null}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          {selectedTerm?.title}
          <Typography variant="caption" display="block" color="text.secondary">
            {selectedTerm && `${getTermTypeLabel(selectedTerm.type)} • Versão ${selectedTerm.version}`}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              '& h1, & h2, & h3': {
                mt: 2,
                mb: 1,
                fontWeight: 'bold',
              },
              '& h1': {
                fontSize: '1.5rem',
              },
              '& h2': {
                fontSize: '1.25rem',
              },
              '& h3': {
                fontSize: '1.1rem',
              },
              '& p': {
                mb: 1,
                lineHeight: 1.6,
              },
              '& ul, & ol': {
                mb: 1,
                pl: 3,
              },
              '& li': {
                mb: 0.5,
                lineHeight: 1.6,
              },
              '& strong': {
                fontWeight: 'bold',
              },
            }}
            dangerouslySetInnerHTML={{ __html: selectedTerm?.content || '' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Fechar</Button>
          {selectedTerm && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedTermIds.has(selectedTerm._id)}
                  onChange={() => {
                    handleTermToggle(selectedTerm._id);
                    handleCloseDialog();
                  }}
                  color="primary"
                />
              }
              label="Aceitar este termo"
            />
          )}
        </DialogActions>
      </Dialog>

      {/* Resumo de aceites */}
      {terms.length > 0 && (
        <Paper sx={{ p: 2, mt: 2, bgcolor: allAccepted ? 'success.light' : 'warning.light' }}>
          <Typography variant="body2" fontWeight="bold">
            Situação dos Aceites
          </Typography>
          <Typography variant="body2">
            {acceptedTermIds.size} de {terms.length} termos aceitos
          </Typography>
          {allAccepted && (
            <Typography variant="body2" color="success.dark" sx={{ mt: 1 }}>
              ✓ Todos os termos foram aceitos. Você pode prosseguir com o cadastro.
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

